/**
 * Login Page with OTP Authentication
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { parsePhoneNumber } from '@/lib/utils';
import { logger } from '@/lib/logger';

export default function LoginPage() {
  const navigate = useNavigate();
  const { sendOTP, verifyOTP, isLoading, error, clearError } = useAuthStore();
  
  const [phone, setPhone] = useState('');
  const [otp, setOTP] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      const formattedPhone = parsePhoneNumber(phone);
      await sendOTP(formattedPhone);
      setStep('otp');
      logger.info('OTP sent', { phone: formattedPhone });
    } catch (error) {
      logger.error('Failed to send OTP', error as Error);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      const formattedPhone = parsePhoneNumber(phone);
      await verifyOTP(formattedPhone, otp);
      logger.info('Login successful');
      navigate('/');
    } catch (error) {
      logger.error('Failed to verify OTP', error as Error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <picture>
                <source srcSet="/frog_logo.webp" type="image/webp" />
                <img 
                  src="/frog_logo.png" 
                  alt="FrogBasket" 
                  className="w-20 h-20 object-contain"
                />
              </picture>
            </div>
            <h1 className="text-3xl font-bold mb-2">
              <span className="text-emerald-600">Frog</span>
              <span className="text-amber-500">Basket</span>
            </h1>
            <p className="text-gray-600">Fresh from your local market</p>
          </div>

        {step === 'phone' ? (
          <form onSubmit={handleSendOTP} className="space-y-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+63 9XX XXX XXXX"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                disabled={isLoading}
              />
              <p className="mt-2 text-sm text-gray-500">
                Enter your Philippine mobile number
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !phone}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP
              </label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOTP(e.target.value)}
                placeholder="6-digit code"
                maxLength={6}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-center text-2xl tracking-widest"
                disabled={isLoading}
                autoFocus
              />
              <p className="mt-2 text-sm text-gray-500">
                We sent a code to {phone}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify & Login'}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setOTP('');
                  clearError();
                }}
                className="w-full text-gray-600 hover:text-gray-800 font-medium py-2"
                disabled={isLoading}
              >
                ← Change number
              </button>
            </div>
          </form>
        )}

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>By continuing, you agree to our Terms & Privacy Policy</p>
          </div>
        </div>
      </div>
    </div>
  );
}
